// Howie Knowledge Graph — Concept Detail Panel
// Slide-over panel showing canonical details, relationships, frameworks, dialectics.
// Modeled on Alexandria's ConceptDetailPanel for visual richness.

import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import type { Canonical, CanonicalPoleInfo, RelatedConcept, FrameworkAssociation } from './types';
import {
  STANCE_COLORS,
  STANCE_LABELS,
  DOMAIN_COLORS,
  DOMAIN_LABELS,
  RELATIONSHIP_LABELS,
} from './constants';
import { RelationshipList } from './RelationshipList';

interface HowieConceptPanelProps {
  canonical: Canonical;
  domainIndex: number;
  poleInfos: CanonicalPoleInfo[];
  relatedConcepts: RelatedConcept[];
  frameworkAssociations: FrameworkAssociation[];
  onClose: () => void;
  onConceptClick: (canonicalId: string) => void;
}

function buildConceptAskPrompt(
  canonical: Canonical,
  poleInfos: CanonicalPoleInfo[],
  frameworkAssociations: FrameworkAssociation[],
): string {
  const lines = [
    `Help me understand "${canonical.name}" in HOW.`,
    '',
    'Use standard Ask mode: ground the response in Dov Seidman\'s HOW, make it concrete, and help me see what this concept asks of me in practice.',
  ];

  if (canonical.description) {
    lines.push('', `Explore context: ${canonical.description}`);
  }

  if (poleInfos.length > 0) {
    lines.push(
      '',
      'Relevant dialectic tensions:',
      ...poleInfos.map((poleInfo) => {
        const pole = poleInfo.pole_name ?? poleInfo.pole_position ?? 'one side';
        return `- ${poleInfo.dialectic.name}: ${poleInfo.dialectic.pole_a} / ${poleInfo.dialectic.pole_b}; this concept appears on the ${pole} side.`;
      }),
    );
  }

  if (frameworkAssociations.length > 0) {
    lines.push(
      '',
      'Connected HOW frameworks:',
      ...frameworkAssociations.map((fa) => `- ${fa.framework.name} (${fa.type.replace(/_/g, ' ')})`),
    );
  }

  lines.push('', 'Please answer as Howie, not as a graph browser: explain the concept, name the live tension, and give me one grounded question I can carry into a real decision.');

  return lines.join('\n');
}

function buildConceptDisplayPrompt(canonical: Canonical): string {
  return `Help me understand "${canonical.name}".`;
}

function buildFrameworkAskPrompt(
  frameworkAssociation: FrameworkAssociation,
  canonical: Canonical,
): string {
  const { framework, type } = frameworkAssociation;
  const lines = [
    `Help me understand the "${framework.name}" framework in HOW.`,
    '',
    'Use standard Ask mode: ground the response in Dov Seidman\'s HOW, explain what the framework is for, and make it practical rather than encyclopedic.',
  ];

  if (framework.description) {
    lines.push('', `Framework context: ${framework.description}`);
  }

  lines.push(
    '',
    `I opened this from the concept "${canonical.name}", where Explore marks the framework relationship as ${type.replace(/_/g, ' ')}.`,
    '',
    'Please explain how this framework helps me read that concept, where it can change behavior, and one question I should ask myself to practice it.',
  );

  return lines.join('\n');
}

function buildFrameworkDisplayPrompt(frameworkAssociation: FrameworkAssociation): string {
  return `Help me understand "${frameworkAssociation.framework.name}".`;
}

export default function HowieConceptPanel({
  canonical,
  domainIndex,
  poleInfos,
  relatedConcepts,
  frameworkAssociations,
  onClose,
  onConceptClick,
}: HowieConceptPanelProps) {
  const navigate = useNavigate();
  const domainColor = DOMAIN_COLORS[domainIndex] ?? DOMAIN_COLORS[0];
  const domainLabel = DOMAIN_LABELS[domainIndex] ?? 'Unknown';

  const handleAskHowie = () => {
    navigate('/chat', {
      state: {
        newConversation: true,
        autoSendMessage: buildConceptAskPrompt(canonical, poleInfos, frameworkAssociations),
        autoSendDisplayMessage: buildConceptDisplayPrompt(canonical),
      },
    });
  };

  const handleAskFramework = (frameworkAssociation: FrameworkAssociation) => {
    navigate('/chat', {
      state: {
        newConversation: true,
        autoSendMessage: buildFrameworkAskPrompt(frameworkAssociation, canonical),
        autoSendDisplayMessage: buildFrameworkDisplayPrompt(frameworkAssociation),
      },
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-over panel */}
      <div
        className="fixed right-0 top-0 h-full z-50 w-[380px] max-w-full bg-white flex flex-col
                   border-l border-gray-100 shadow-2xl
                   transition-transform duration-300 translate-x-0"
        style={{ boxShadow: '-8px 0 30px rgba(0,0,0,0.12)' }}
      >
        {/* ── Header (sticky) ──────────────────────────────────────────────── */}
        <div className="p-5 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-bold text-xl leading-tight flex-1" style={{ color: 'var(--how-space-deep)' }}>
              {canonical.name}
            </h2>
            <button
              onClick={onClose}
              className="shrink-0 text-gray-300 hover:text-gray-500 transition-colors mt-0.5"
              aria-label="Close panel"
            >
              <X size={20} />
            </button>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            <span
              className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
              style={{ background: `${domainColor}20`, color: domainColor }}
            >
              {domainLabel}
            </span>
            {canonical.stance && (
              <span
                className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold text-white uppercase tracking-wide"
                style={{ backgroundColor: STANCE_COLORS[canonical.stance] }}
              >
                {STANCE_LABELS[canonical.stance]}
              </span>
            )}
            {canonical.importance_score != null && canonical.importance_score > 0 && (
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium text-gray-400 bg-gray-50">
                {Math.round(canonical.importance_score * 100)}% importance
              </span>
            )}
          </div>
        </div>

        {/* ── Scrollable content ───────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">

          {/* Description */}
          {canonical.description && (
            <Section title="Description">
              <p className="text-sm text-gray-500 leading-relaxed">
                {canonical.description}
              </p>
            </Section>
          )}

          {/* Related Concepts */}
          {relatedConcepts.length > 0 && (
            <Section title={`Related Concepts (${relatedConcepts.length})`}>
              <RelationshipList
                relatedConcepts={relatedConcepts}
                onConceptClick={onConceptClick}
              />
            </Section>
          )}

          {/* Dialectic Tensions */}
          {poleInfos.length > 0 && (
            <Section title="Dialectic Tensions">
              <div className="flex flex-col gap-2.5">
                {poleInfos.map((poleInfo, idx) => (
                  <div
                    key={poleInfo.dialectic.id + idx}
                    className="p-3 rounded-lg bg-gray-50 flex flex-col gap-1"
                    style={{ borderLeft: `2px solid ${domainColor}50` }}
                  >
                    <p className="text-sm" style={{ color: 'var(--how-space-deep)' }}>
                      On the{' '}
                      <span className="font-medium">
                        {poleInfo.pole_name ?? poleInfo.pole_position ?? 'unknown'}
                      </span>{' '}
                      side of{' '}
                      <span className="font-medium">{poleInfo.dialectic.name}</span>
                    </p>
                    <p className="text-xs text-gray-400">
                      {poleInfo.dialectic.pole_a}{' '}
                      <span className="text-gray-300 mx-0.5">&larr;</span>{' '}
                      {poleInfo.dialectic.name}{' '}
                      <span className="text-gray-300 mx-0.5">&rarr;</span>{' '}
                      {poleInfo.dialectic.pole_b}
                    </p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Frameworks */}
          {frameworkAssociations.length > 0 && (
            <Section title="Frameworks">
              <div className="flex flex-col gap-1.5">
                {frameworkAssociations.map((fa, idx) => (
                  <button
                    key={fa.framework.id + idx}
                    onClick={() => handleAskFramework(fa)}
                    className="flex w-full items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2 text-left transition-colors hover:border-how-accent-warm/40 hover:bg-how-accent-warm/5"
                  >
                    <span className="text-sm font-medium flex-1" style={{ color: 'var(--how-space-deep)' }}>
                      {fa.framework.name}
                    </span>
                    <span className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold">
                      {RELATIONSHIP_LABELS[fa.type] ?? fa.type.replace(/_/g, ' ')}
                    </span>
                  </button>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* ── Ask Howie button (pinned to bottom) ──────────────────────────── */}
        <div className="p-5 border-t border-gray-100">
          <button
            onClick={handleAskHowie}
            className="w-full py-2.5 px-4 rounded-lg text-sm font-semibold text-white
                       transition-opacity hover:opacity-90 active:opacity-80"
            style={{ backgroundColor: 'var(--how-accent-warm)' }}
          >
            Ask Howie about this &rarr;
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Section helper ─────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        {title}
      </h3>
      {children}
    </div>
  );
}
